import {
  ApiError,
  attachments,
  auth,
  call,
  comments,
  cycles,
  items,
  meta,
  notifications,
  projects,
  states,
  transportProblem,
  workspaces,
  fillPath,
  type Attachment,
  type CommentRevision,
  type Cycle,
  type FeedPage,
  type GroupKeyValue,
  type InstanceInfo,
  type Label,
  type MemberView,
  type Notification,
  type Page,
  type Project,
  type ProjectMemberView,
  type ProjectSummary,
  type Schema,
  type State,
  type UnreadCount,
  type WatcherView,
  type Workspace,
  type WorkItem,
} from "@baton/api";
import { FEED_LIMIT, keys, LIST_LIMIT, normaliseQuery, type CursorPageParam, type GroupedPage, type GroupedPageParam, type ListScope, type NotificationFilter, type Runtime } from "@baton/core";
import { STATE_GROUPS } from "@baton/ui";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { followMovedTeams } from "../shell/state/teamAddress.ts";

/**
 * The query options every authenticated screen reads (`contracts/client-api.md` § Query
 * cache): one place spells the operation, the key from `@baton/core`'s scheme and the
 * shape, so no screen builds a key or a request of its own. Every read goes through the
 * signed-in client, whose 401 parks the request behind the re-authentication dialog
 * (FR-015-013). Lists use cursor pagination with `useInfiniteQuery` (§ Pagination and
 * infinite scroll); a grouped item list is two-dimensional, with per-group cursors.
 */
export type MeView = Schema<"MeView">;
export type MyInvitations = Schema<"MyInvitations">;
export type MyInvitation = Schema<"MyInvitation">;
export type AddressAvailability = Schema<"AddressAvailability">;
export type FormerAddress = Schema<"FormerAddress">;

/** The list bodies 001 answers `{ items, next_cursor, total }` where `openapi.json` still
 * says `object`; the shape is checked at the edge rather than asserted. */
export function pageOf<T>(data: unknown): Page<T> {
  if (typeof data !== "object" || data === null || !Array.isArray((data as { items?: unknown }).items)) {
    throw new ApiError(transportProblem(0, ""));
  }
  const body = data as { items: T[]; next_cursor?: string | null; total?: number | null; warnings?: string[] };
  return { items: body.items, next_cursor: body.next_cursor ?? null, total: body.total ?? null, ...(Array.isArray(body.warnings) ? { warnings: body.warnings } : {}) };
}

export function instanceQuery(runtime: Runtime) {
  return queryOptions({
    queryKey: keys.global.instance(),
    queryFn: async ({ signal }): Promise<InstanceInfo> => (await meta.instance(runtime.client, { signal })).data,
  });
}

/**
 * `GET /me`, which is also where this device learns each Team's current address: a Team whose
 * address changed since the device last saw it has what the device keeps under the old address
 * moved to the new one before any screen reads it (001 FR-157).
 */
export function meQuery(runtime: Runtime) {
  return queryOptions({
    queryKey: keys.global.me(),
    queryFn: async ({ signal }): Promise<MeView> => {
      const me = (await meta.me(runtime.signedIn, { signal })).data;
      followMovedTeams(runtime, me.memberships);
      return me;
    },
  });
}

/** 001 FR-156: whether `slug` is free for a Team, and a free variant of one that is not. A result is kept a few seconds, so typing back to an address asked about moments ago asks again only once it may have changed. */
export function addressAvailabilityQuery(runtime: Runtime, slug: string) {
  return queryOptions({
    queryKey: keys.global.workspaceAddress(slug),
    queryFn: async ({ signal }): Promise<AddressAvailability> => (await workspaces.addressAvailability(runtime.signedIn, { params: { slug }, signal, retry: false })).data,
    staleTime: 5_000,
  });
}

/** 001 FR-157: which of the person's Teams moved away from `slug`, for the page an old link opens; a 404 when none did. */
export function formerAddressQuery(runtime: Runtime, slug: string) {
  return queryOptions({
    queryKey: keys.global.formerAddress(slug),
    queryFn: async ({ signal }): Promise<FormerAddress> => (await workspaces.formerAddress(runtime.signedIn, { params: { slug }, signal, retry: false })).data,
    retry: false,
  });
}

/** 001 FR-155: the pending invitations of the person's own verified address, which the welcome screen asks about. */
export function invitationsQuery(runtime: Runtime) {
  return queryOptions({
    queryKey: keys.global.invitations(),
    queryFn: async ({ signal }): Promise<MyInvitations> => (await auth.myInvitations(runtime.signedIn, { signal })).data,
  });
}

export function workspacesQuery(runtime: Runtime) {
  return queryOptions({
    queryKey: keys.global.workspaces(),
    queryFn: async ({ signal }): Promise<Page<Workspace>> => pageOf<Workspace>((await workspaces.list(runtime.signedIn, { signal })).data),
  });
}

export function workspaceQuery(runtime: Runtime, ws: string) {
  return queryOptions({
    queryKey: keys.ws(ws),
    queryFn: async ({ signal }): Promise<Workspace> => (await workspaces.get(runtime.signedIn, { params: { ws }, signal })).data,
  });
}

export function projectsQuery(runtime: Runtime, ws: string, archived = false) {
  return queryOptions({
    queryKey: archived ? ([...keys.projects(ws), { archived: "true" }] as const) : keys.projects(ws),
    // Switching between the live and the archived projects keeps the last list drawn (FR-015-194).
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<Page<Project>> => pageOf<Project>((await projects.list(runtime.signedIn, { params: { ws }, query: archived ? { archived: "true" } : {}, signal })).data),
  });
}

export function projectQuery(runtime: Runtime, ws: string, project: string) {
  return queryOptions({
    queryKey: keys.project(ws, project),
    queryFn: async ({ signal }): Promise<Project> => (await projects.get(runtime.signedIn, { params: { ws, project }, signal })).data,
  });
}

export function projectSummaryQuery(runtime: Runtime, ws: string, project: string) {
  return queryOptions({
    queryKey: keys.projectSub(ws, project, "summary"),
    queryFn: async ({ signal }): Promise<ProjectSummary> => (await projects.summary(runtime.signedIn, { params: { ws, project }, signal })).data,
  });
}

export function projectActivityQuery(runtime: Runtime, ws: string, project: string) {
  return infiniteQueryOptions({
    queryKey: keys.projectSub(ws, project, "activity"),
    initialPageParam: { cursor: null },
    getNextPageParam: (last: FeedPage): CursorPageParam | undefined => (last.next_cursor === null || last.next_cursor === undefined ? undefined : { cursor: last.next_cursor }),
    queryFn: async ({ pageParam, signal }): Promise<FeedPage> => (await projects.activity(runtime.signedIn, { params: { ws, project }, query: { limit: FEED_LIMIT, ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) }, signal })).data,
  });
}

/**
 * The Team's states, one ordered set every project's tasks take (001 FR-030, 2026-09-30, by the owner's
 * decision: "well we need it"). Every picker, grouping, prefill and header reads this one key, whatever
 * the project or the scope, so a list spanning projects names the same states as a project's.
 */
export function statesQuery(runtime: Runtime, ws: string) {
  return queryOptions({
    queryKey: keys.states(ws),
    queryFn: async ({ signal }): Promise<readonly State[]> => pageOf<State>((await states.list(runtime.signedIn, { params: { ws }, signal })).data).items,
  });
}

export function projectLabelsQuery(runtime: Runtime, ws: string, project: string) {
  return queryOptions({
    queryKey: keys.projectSub(ws, project, "labels"),
    queryFn: async ({ signal }): Promise<readonly Label[]> => pageOf<Label>((await projects.labels(runtime.signedIn, { params: { ws, project }, signal })).data).items,
  });
}

/**
 * Whether the caller reads a project's member lists, `project.member.read`, which every role of
 * the Team holds but a guest (010 § Project role: guest). A guest's member lists are read as empty
 * without asking, since the server refuses them, so no picker a guest opens asks for them
 * (2026-09-29: a guest's inline composer asked fifteen times a second).
 */
export function readsProjectMembers(me: Pick<MeView, "memberships"> | undefined, ws: string): boolean {
  const role = me?.memberships.find((membership) => membership.slug === ws)?.role;
  return role !== undefined && role !== "guest";
}

/** A project member list for a caller who reads it, and none for one who does not (`readsProjectMembers`). */
async function membersIfRead(runtime: Runtime, ws: string, read: () => Promise<readonly ProjectMemberView[]>): Promise<readonly ProjectMemberView[]> {
  const me = await runtime.queryClient.query({ ...meQuery(runtime), staleTime: "static" });
  return readsProjectMembers(me, ws) ? read() : [];
}

/**
 * The people who reach a project (001 FR-022, amended 2026-09-28): its member rows and the Team's
 * owners and admins, each with its `source`. Every people picker of the project that is not an
 * assignee picker reads this: a module's people, a watcher, a mention, a people field, the lead.
 */
export function projectMembersQuery(runtime: Runtime, ws: string, project: string) {
  return queryOptions({
    queryKey: keys.projectSub(ws, project, "members"),
    queryFn: ({ signal }): Promise<readonly ProjectMemberView[]> => membersIfRead(runtime, ws, async () => pageOf<ProjectMemberView>((await projects.members(runtime.signedIn, { params: { ws, project }, signal })).data).items),
  });
}

/**
 * Everyone who may be assigned a task of a project (001 FR-022, amended 2026-09-28, the owner's
 * "everyone should show up in tasks"): every active member of the Team, row or none, and the guests
 * and agents holding a row. Every assignee picker reads this, and the server accepts exactly this set.
 */
export function projectAssignableQuery(runtime: Runtime, ws: string, project: string) {
  return queryOptions({
    queryKey: keys.projectSub(ws, project, "assignable"),
    queryFn: ({ signal }): Promise<readonly ProjectMemberView[]> => membersIfRead(runtime, ws, async () => pageOf<ProjectMemberView>((await projects.members(runtime.signedIn, { params: { ws, project }, query: { assignable: true }, signal })).data).items),
  });
}

/** Every cycle of the team, any status: the Transfer dialog's target picker reads this
 * and filters client-side, since a sprint is the team's now, not one project's. */
export function cyclesQuery(runtime: Runtime, ws: string) {
  return queryOptions({
    queryKey: keys.cycles(ws),
    queryFn: async ({ signal }): Promise<readonly Cycle[]> => pageOf<Cycle>((await cycles.list(runtime.signedIn, { params: { ws }, signal })).data).items,
  });
}

export function workspaceMembersQuery(runtime: Runtime, ws: string, filter: { readonly kind?: "user" | "agent"; readonly q?: string } = {}) {
  return queryOptions({
    queryKey: keys.members(ws, filter),
    // A changed kind or search keeps the last list drawn until the next arrives (FR-015-194).
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<readonly MemberView[]> => pageOf<MemberView>((await workspaces.members(runtime.signedIn, { params: { ws }, query: { ...(filter.kind === undefined ? {} : { kind: filter.kind }), ...(filter.q === undefined ? {} : { q: filter.q }) }, signal })).data).items,
  });
}

export type AgentView = Schema<"AgentView">;

/**
 * `GET /workspaces/{ws}/agents` (001 § Agents) through the generated `list_agents` operation.
 * 001 answers every agent of the workspace in one body, with `next_cursor` null and no paging
 * parameters, so one read is the whole list.
 */
export function agentsQuery(runtime: Runtime, ws: string) {
  return queryOptions({
    queryKey: keys.agents(ws),
    queryFn: async ({ signal }): Promise<readonly AgentView[]> => (await call(runtime.signedIn, "get", "/api/v1/workspaces/{ws}/agents", { params: { ws }, signal })).data.items,
  });
}

/**
 * The item entity (`contracts/client-api.md` § Key scheme: one key per item). `ref` is the
 * id or the identifier the route carries; the read is keyed by it, and when it was an
 * identifier the same body is written under the id key too, which is the key the event
 * reducer and every list write through. `expand=transitions` is sent so the state control
 * can grey out the targets the graph forbids (§ Properties panel).
 */
const ITEM_PATH = "/api/v1/workspaces/{ws}/items/{item}";

/**
 * `GET /workspaces/{ws}/items/{item}?expand=transitions`. The committed `openapi.json`
 * declares no `expand` query parameter on the single read, although 001 § Item read shape
 * says the item read takes it (a contract defect raised against 001), so the request is
 * formed through the wrapper's `request()` with the generated path rather than the typed
 * operation, which would refuse the parameter.
 */
export async function readItem(runtime: Runtime, ws: string, ref: string, signal?: AbortSignal): Promise<WorkItem> {
  const result = await runtime.signedIn.request<WorkItem>({ path: fillPath(ITEM_PATH, { ws, item: ref }), query: { expand: "transitions" }, ...(signal === undefined ? {} : { signal }) });
  return result.data;
}

export function itemQuery(runtime: Runtime, ws: string, ref: string) {
  return queryOptions({
    queryKey: keys.item(ws, ref),
    queryFn: async ({ signal }): Promise<WorkItem> => {
      const item = await readItem(runtime, ws, ref, signal);
      if (item.id !== ref) runtime.queryClient.setQueryData(keys.item(ws, item.id), item);
      return item;
    },
  });
}

export function itemActivityQuery(runtime: Runtime, ws: string, itemId: string) {
  return infiniteQueryOptions({
    queryKey: keys.itemSub(ws, itemId, "activity"),
    initialPageParam: { cursor: null },
    getNextPageParam: (last: FeedPage): CursorPageParam | undefined => (last.next_cursor === null || last.next_cursor === undefined ? undefined : { cursor: last.next_cursor }),
    queryFn: async ({ pageParam, signal }): Promise<FeedPage> => (await items.activity(runtime.signedIn, { params: { ws, item: itemId }, query: { limit: FEED_LIMIT, ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) }, signal })).data,
  });
}

export function itemChildrenQuery(runtime: Runtime, ws: string, itemId: string) {
  return queryOptions({
    queryKey: keys.itemSub(ws, itemId, "children"),
    queryFn: async ({ signal }): Promise<readonly WorkItem[]> => (await items.children(runtime.signedIn, { params: { ws, item: itemId }, signal })).data.items,
  });
}

export function itemWatchersQuery(runtime: Runtime, ws: string, itemId: string) {
  return queryOptions({
    queryKey: keys.itemSub(ws, itemId, "watchers"),
    queryFn: async ({ signal }): Promise<{ readonly items: readonly WatcherView[]; readonly total: number }> => {
      const body = (await items.watchers(runtime.signedIn, { params: { ws, item: itemId }, signal })).data;
      return { items: body.items, total: body.total };
    },
  });
}

/** Signed download and thumbnail URLs expire in fifteen minutes, so this read is short-lived (§ File uploads). */
export const ATTACHMENT_STALE_MS = 5 * 60_000;

/**
 * An attachment that a body still references, or the fact that it is gone (2026-09-21, by the
 * owner's decision, after a description was found pointing at attachments that had been deleted).
 */
export type ReferencedAttachment = { readonly kind: "ready"; readonly attachment: Attachment } | { readonly kind: "gone" };

/**
 * A 404 or a 410 on an attachment a body references is an answer, not a failure: the file was
 * deleted and an attachment id is never reused, so the reference will not come back. Resolving it
 * rather than throwing is what stops the reference costing a request on every render — an errored
 * query is refetched on the next mount, so a description carrying three dead references asked for
 * three 404s every time the task was opened, forever. As a cached answer it is asked again at most
 * once per `ATTACHMENT_STALE_MS`, and the renderer has something true to say instead of a broken
 * image. Any other failure still throws, because a network blip or a 500 may well come back.
 */
export function attachmentQuery(runtime: Runtime, ws: string, attachmentId: string) {
  return queryOptions({
    queryKey: keys.attachment(ws, attachmentId),
    staleTime: ATTACHMENT_STALE_MS,
    queryFn: async ({ signal }): Promise<ReferencedAttachment> => {
      try {
        return { kind: "ready", attachment: (await attachments.get(runtime.signedIn, { params: { ws, id: attachmentId }, signal })).data };
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 410)) return { kind: "gone" };
        throw error;
      }
    },
  });
}

export function commentRevisionsQuery(runtime: Runtime, ws: string, commentId: string) {
  return queryOptions({
    queryKey: keys.commentRevisions(ws, commentId),
    queryFn: async ({ signal }): Promise<readonly CommentRevision[]> => (await comments.revisions(runtime.signedIn, { params: { ws, id: commentId }, signal })).data.items,
  });
}

export function unreadCountQuery(runtime: Runtime, ws: string) {
  return queryOptions({
    queryKey: keys.unreadCount(ws),
    queryFn: async ({ signal }): Promise<UnreadCount> => (await notifications.unreadCount(runtime.signedIn, { params: { ws }, signal })).data,
  });
}

/** The inbox list for one filter; `all` sends no filter parameter. The last filter's rows stay on screen while the next filter's read is out (FR-015-194): the region's subject, the inbox, is the same. */
export function notificationsQuery(runtime: Runtime, ws: string, filter: NotificationFilter | "all") {
  return infiniteQueryOptions({
    queryKey: keys.notifications(ws, filter),
    placeholderData: keepPreviousData,
    initialPageParam: { cursor: null },
    getNextPageParam: (last: Page<Notification>): CursorPageParam | undefined => (last.next_cursor === null ? undefined : { cursor: last.next_cursor }),
    queryFn: async ({ pageParam, signal }): Promise<Page<Notification>> =>
      pageOf<Notification>(
        (await notifications.list(runtime.signedIn, { params: { ws }, query: { limit: FEED_LIMIT, ...(filter === "all" ? {} : { filter }), ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) }, signal })).data,
      ),
  });
}

/** The 001 list query a collection sends, in the contract's parameter names (§ List query). */
export type ItemListParams = Readonly<Record<string, string | readonly string[] | boolean | number | undefined>>;

export type ItemGroupedPage = GroupedPage<WorkItem, GroupKeyValue>;

function listRequest(runtime: Runtime, ws: string, scope: ListScope, query: Record<string, string | readonly string[] | boolean | number | undefined>, signal: AbortSignal | undefined) {
  if (scope.kind === "project" && scope.id !== undefined) {
    return items.listInProject(runtime.signedIn, { params: { ws, project: scope.id }, query, ...(signal === undefined ? {} : { signal }) });
  }
  return items.listInWorkspace(runtime.signedIn, { params: { ws }, query, ...(signal === undefined ? {} : { signal }) });
}

/** The state groups of the work still to do: every group but the two that close a task (001 FR-030). */
export const OPEN_STATE_GROUPS: readonly string[] = STATE_GROUPS.filter((group) => group !== "completed" && group !== "cancelled");

/** What every collection read expands (`contracts/client-api.md` § Pagination and infinite scroll), joined by what the caller's own `expand` names (the timeline's `relations`, 2026-09-18). */
const LIST_EXPAND = ["transitions", "children", "attachments"] as const;

function expandOf(params: ItemListParams): readonly string[] {
  const own = params.expand;
  const extra = typeof own === "string" ? own.split(",") : Array.isArray(own) ? (own as readonly string[]) : [];
  return [...new Set([...LIST_EXPAND, ...extra.map((value) => value.trim()).filter((value) => value.length > 0)])];
}

/**
 * The filters a scope carries that are not the caller's to drop: a sprint's list is that sprint's
 * items and a module's list that module's, whatever the filter bar says. Every request over a scope
 * applies them, the rows and the bulk bar's total alike (2026-09-18: the total was read without
 * them, so a sprint of 25 tasks answered "1 of 93 selected", the whole team's count). A client's own
 * task list reads its associated tasks under `associated_account` (020 FR-020-306). Client tasks'
 * scope is the tasks that report a client, which the reader's own Client filter, when it names
 * clients, says in its place (2026-10-13), so the scope reads the request's parameters too.
 */
export function scopeFilters(scope: ListScope, params: ItemListParams = {}): { readonly cycle?: readonly string[]; readonly module?: readonly string[]; readonly account?: readonly string[]; readonly associated_account?: string; readonly state_group?: readonly string[] } {
  const chosenClients = params.account;
  const namesClients = typeof chosenClients === "string" ? chosenClients.length > 0 : Array.isArray(chosenClients) && chosenClients.length > 0;
  return {
    // Client tasks are the tasks that report a client, unless the reader's Client filter names which (2026-10-13).
    ...(scope.clientTasks === true && !namesClients ? { account: ["!none"] } : {}),
    ...(scope.cycleId === undefined ? {} : { cycle: [scope.cycleId] }),
    // The Team's backlog is its open tasks no sprint holds (015 § Backlog, 2026-10-02).
    ...(scope.backlog === true ? { cycle: ["none"] } : {}),
    ...(scope.backlog === true || scope.openOnly === true ? { state_group: OPEN_STATE_GROUPS } : {}),
    ...(scope.moduleId === undefined ? {} : { module: [scope.moduleId] }),
    // A client's task list is the client's associated tasks, those that report it and those that link it or one of its contacts (020 FR-020-306).
    ...(scope.accountId === undefined ? {} : { associated_account: scope.accountId }),
  };
}

/**
 * The scope's own filters and the list's `expand`, added to any read over the 001 item list query (the
 * timeline shares them). A scope that holds open tasks only keeps the reader's own state group filter
 * inside the open groups rather than replacing it (2026-10-03), so a Started chip on My work or the
 * backlog narrows the list to the started tasks; a chip naming only closed groups, which those lists'
 * filters do not offer, leaves the scope's open groups.
 */
export function scopedParams(scope: ListScope, params: ItemListParams): Record<string, string | readonly string[] | boolean | number | undefined> {
  const scoped = scopeFilters(scope, params);
  const chosen = params.state_group;
  const chosenGroups = typeof chosen === "string" ? [chosen] : Array.isArray(chosen) ? (chosen as readonly string[]) : [];
  const narrowed = scoped.state_group?.filter((group) => chosenGroups.includes(group)) ?? [];
  return { ...params, expand: expandOf(params), ...scoped, ...(narrowed.length > 0 ? { state_group: narrowed } : {}) };
}

/**
 * A grouped item list: pages are stored under one list key and a page either adds groups
 * (the collection's Load more, `group_cursor`) or extends one group (that group's Load
 * more, `cursor`); `mergeGroups` folds them back into one ordered set (§ Pagination and
 * infinite scroll). `expand=transitions,children,attachments` rides along so the inline state
 * chip and the state-grouped drag targets know what the graph forbids, the sub-item caret
 * shows on the first paint, and a board card has its cover image and a row its attachment
 * count; 001 § Item list rows carries those members only when `expand` names them.
 */
export function itemsGroupedQuery(runtime: Runtime, ws: string, scope: ListScope, params: ItemListParams) {
  const full = { ...scopedParams(scope, params), limit: LIST_LIMIT, group_limit: LIST_LIMIT };
  return infiniteQueryOptions({
    queryKey: keys.itemsList(ws, scope, normaliseQuery(full)),
    initialPageParam: { groupCursor: null },
    getNextPageParam: (last: ItemGroupedPage): GroupedPageParam | undefined => (last.next_group_cursor === null ? undefined : { groupCursor: last.next_group_cursor }),
    queryFn: async ({ pageParam, signal }): Promise<ItemGroupedPage> => {
      const query = {
        ...full,
        ...(pageParam.group === undefined ? {} : { cursor: pageParam.group.cursor }),
        ...(pageParam.groupCursor === null || pageParam.group !== undefined ? {} : { group_cursor: pageParam.groupCursor }),
      };
      const body = (await listRequest(runtime, ws, scope, query, signal)).data as { groups?: { key: GroupKeyValue | null; count: number; items: WorkItem[]; next_cursor?: string | null }[]; next_group_cursor?: string | null; multi?: boolean; warnings?: string[] };
      if (!Array.isArray(body.groups)) throw new ApiError(transportProblem(0, ""));
      return { groups: body.groups.map((group) => ({ key: group.key, count: group.count, items: group.items, next_cursor: group.next_cursor ?? null })), next_group_cursor: body.next_group_cursor ?? null, ...(body.multi === undefined ? {} : { multi: body.multi }), ...(Array.isArray(body.warnings) ? { warnings: body.warnings } : {}) };
    },
  });
}

/** A flat item list (`group_by=none`): ordinary cursor pagination. */
export function itemsFlatQuery(runtime: Runtime, ws: string, scope: ListScope, params: ItemListParams, limit = LIST_LIMIT) {
  const full = { ...scopedParams(scope, params), limit };
  return infiniteQueryOptions({
    queryKey: keys.itemsList(ws, scope, normaliseQuery(full)),
    initialPageParam: { cursor: null },
    getNextPageParam: (last: Page<WorkItem>): CursorPageParam | undefined => (last.next_cursor === null ? undefined : { cursor: last.next_cursor }),
    queryFn: async ({ pageParam, signal }): Promise<Page<WorkItem>> => pageOf<WorkItem>((await listRequest(runtime, ws, scope, { ...full, ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) }, signal)).data),
  });
}

/** One page of a workspace or project item search, for the item picker and the palette (`q=`). */
export function itemSearchQuery(runtime: Runtime, ws: string, q: string, projects: readonly string[] = []) {
  const query = { q, limit: 20, ...(projects.length === 0 ? {} : { projects: [...projects] }) };
  return queryOptions({
    queryKey: keys.itemsList(ws, { kind: "workspace" }, normaliseQuery({ q, limit: 20, ...(projects.length === 0 ? {} : { projects: projects.join(",") }) })),
    staleTime: 0,
    queryFn: async ({ signal }): Promise<readonly WorkItem[]> => pageOf<WorkItem>((await items.listInWorkspace(runtime.signedIn, { params: { ws }, query, signal })).data).items,
  });
}
